import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, X, Minus } from "lucide-react";
import { AppFrame } from "@/components/AppFrame";
import { cachedGet, getFresh, todayStr, fmt12 } from "@/lib/api";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const Mark = ({ status, testid }) => {
  if (status === "taken")
    return <Check data-testid={testid} className="mx-auto h-[18px] w-[18px] text-emerald-400" />;
  if (status === "skipped") return <X data-testid={testid} className="mx-auto h-[18px] w-[18px] text-red-400" />;
  if (status === "snoozed")
    return <Minus data-testid={testid} className="mx-auto h-[18px] w-[18px] text-amber-400" />;
  return <Minus data-testid={testid} className="mx-auto h-[18px] w-[18px] text-slate-600" />;
};

export default function ComplianceGrid() {
  const today = todayStr();
  const [data, setData] = useState(() => cachedGet("/compliance", { end: today, days: 7 }) || null);
  const navigate = useNavigate();

  useEffect(() => {
    getFresh("/compliance", { end: today, days: 7 })
      .then(setData)
      .catch(() => {
        toast.error("Could not load compliance");
        setData((prev) => prev || { dates: [], medicines: [], failed: true });
      });
  }, [today]);

  const dates = [...(data?.dates || [])].reverse(); // today first

  return (
    <AppFrame title="Compliance">
      {!data ? (
        <div className="space-y-3 pt-2">
          {[0, 1].map((i) => (
            <div key={i} className="h-28 animate-pulse rounded-2xl bg-slate-900/60" />
          ))}
        </div>
      ) : data.medicines.length === 0 ? (
        <p className="py-14 text-center text-[15px] text-slate-400" data-testid="empty-compliance">
          {data.failed ? "Could not load compliance. Reopen this tab to retry." : "Add a medicine to track compliance."}
        </p>
      ) : (
        <div className="space-y-3 pb-4" data-testid="compliance-list">
          {data.medicines.map((m) => (
            <div
              key={m.med_id}
              data-testid={`compliance-card-${m.med_id}`}
              onClick={() => navigate(`/compliance/${m.med_id}`)}
              className="cursor-pointer rounded-2xl border border-slate-800 bg-slate-900/45 p-3.5 transition-colors duration-200 active:bg-slate-900/70"
            >
              <h3 className="mb-2.5 truncate text-[17px] font-semibold">{m.name}</h3>

              <table className="w-full table-fixed">
                <thead>
                  <tr>
                    <th className="w-[62px] pb-1.5 text-left text-[12px] font-medium text-slate-500">Time</th>
                    {dates.map((d) => (
                      <th
                        key={d}
                        className={`pb-1.5 text-center text-[12px] font-medium ${
                          d === today ? "text-sky-300" : "text-slate-500"
                        }`}
                      >
                        {d === today ? "Today" : DOW[new Date(d + "T12:00:00").getDay()]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {m.times.map((t) => (
                    <tr key={t}>
                      <td className="py-1.5 text-left text-[13px] text-slate-400">{fmt12(t)}</td>
                      {dates.map((d) => {
                        const dow = new Date(d + "T12:00:00").getDay();
                        const begin = m.start_date || m.created_at.slice(0, 10);
                        const skip =
                          d < begin ||
                          (m.end_date && d > m.end_date) ||
                          !(m.days || [0, 1, 2, 3, 4, 5, 6]).includes(dow);
                        return (
                          <td key={d} className="py-1.5">
                            {skip ? (
                              <span
                                className="block text-center text-[13px] text-slate-700"
                                data-testid={`skip-${m.med_id}-${d}-${t}`}
                              >
                                {d < (m.start_date || m.created_at.slice(0, 10)) ? "·" : ""}
                              </span>
                            ) : (
                              <Mark status={m.logs?.[d]?.[t] || "none"} testid={`mark-${m.med_id}-${d}-${t}`} />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </AppFrame>
  );
}
